/**
 * kpiStripGrouper.ts
 *
 * Pure utility: given an ordered list of panels, produces rendering groups.
 * Implements the KPI Strip adjacency rule from brand guide §13 / panel-library-design §4.2:
 *
 *   - 2–6 consecutive `kpi` panels → one `KpiStripGroup` (max 6 cells per strip)
 *   - 7th+ kpi in the same run → starts a new group as a solo hero card
 *   - Single kpi (not adjacent to another kpi) → `KpiSoloGroup`
 *   - Any non-kpi panel → `SinglePanelGroup`
 *
 * This is a pure function — no React, no Redux. Tested in isolation.
 */

import type { PanelType } from '@/types/DashboardV1.types'

/** Maximum cells rendered in one KPI strip card. */
export const KPI_STRIP_MAX_CELLS = 6

// ─── Panel subset interface (only what the grouper needs) ─────────────────────

export interface GroupablePanel {
  id: string
  panelType: PanelType | string
}

// ─── Group types ──────────────────────────────────────────────────────────────

/** 2–6 consecutive KPI panels rendered as one horizontal strip card. */
export interface KpiStripGroup {
  kind: 'kpi_strip'
  panels: GroupablePanel[]
}

/** Single KPI panel rendered as a solo hero card (text-5xl value). */
export interface KpiSoloGroup {
  kind: 'kpi_solo'
  panel: GroupablePanel
}

/** Any non-KPI panel rendered individually. */
export interface SinglePanelGroup {
  kind: 'single'
  panel: GroupablePanel
}

export type PanelGroup = KpiStripGroup | KpiSoloGroup | SinglePanelGroup

// ─── groupPanels ──────────────────────────────────────────────────────────────

/**
 * Takes an ordered panel array and returns rendering groups per the KPI Strip
 * adjacency rule.
 *
 * @param panels - All panels in canvas order.
 * @returns Ordered array of `PanelGroup` items ready for render dispatch.
 */
export function groupPanels(panels: GroupablePanel[]): PanelGroup[] {
  if (panels.length === 0) return []

  const groups: PanelGroup[] = []
  let i = 0

  while (i < panels.length) {
    const panel = panels[i]

    if (panel.panelType !== 'kpi') {
      groups.push({ kind: 'single', panel })
      i++
      continue
    }

    // Scan forward to find the run of consecutive kpi panels
    let runEnd = i + 1
    while (runEnd < panels.length && panels[runEnd].panelType === 'kpi') {
      runEnd++
    }

    const run = panels.slice(i, runEnd)

    if (run.length === 1) {
      // Solo hero card
      groups.push({ kind: 'kpi_solo', panel: run[0] })
      i++
      continue
    }

    // Split into strips of KPI_STRIP_MAX_CELLS (2–6 each).
    // A run of 7 → strip[6] + solo[1]
    // A run of 8 → strip[6] + strip[2]
    // A run of 12 → strip[6] + strip[6]
    let j = 0
    while (j < run.length) {
      const remaining = run.length - j
      if (remaining === 1) {
        // Cannot form a 2-cell strip — solo hero
        groups.push({ kind: 'kpi_solo', panel: run[j] })
        j++
      } else {
        const chunkSize = Math.min(remaining, KPI_STRIP_MAX_CELLS)
        groups.push({ kind: 'kpi_strip', panels: run.slice(j, j + chunkSize) })
        j += chunkSize
      }
    }

    i = runEnd
  }

  return groups
}
